import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
    Flag,
    UserCheck,
    Calendar,
    Eye,
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    Download,
    DollarSign,
    Star,
    Edit,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import { format } from "date-fns";

export default function MyTicket() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(null);
    const [editingRequest, setEditingRequest] = useState(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const fileInputRef = useRef(null);

    // جلب طلبات العميل
    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/requests/my-requests");
            if (res.data.success) {
                setRequests(res.data.data || []);
            }
        } catch (err) {
            Swal.fire("Error", "Failed to load your requests.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // فتح التفاصيل
    const openDetails = (request) => {
        setShowDetails(request);
    };

    const closeDetails = () => {
        setShowDetails(null);
    };

    // فتح نموذج التعديل
    const openEditForm = (request) => {
        setEditingRequest({ ...request });
        setShowEditForm(true);
        setShowDetails(null);
    };

    const closeEditForm = () => {
        setShowEditForm(false);
        setEditingRequest(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // دفع عبر Stripe Checkout
    const handlePay = async (requestId) => {
        const confirm = await Swal.fire({
            title: "Confirm Payment",
            text: "You will be redirected to the secure payment page.",
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Proceed to Pay",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#10b981",
        });
        if (!confirm.isConfirmed) return;

        Swal.fire({
            title: "Creating secure checkout...",
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            const res = await api.post(`/payments/request/${requestId}/create-checkout`);
            if (res.data.success && res.data.data?.checkout_url) {
                Swal.close();
                window.location.href = res.data.data.checkout_url;
            } else {
                throw new Error(res.data.message || "Invalid response from server.");
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create payment session. Please try again.";
            Swal.fire("Payment Error", msg, "error");
        }
    };

    // تقييم
    const handleRate = (lawyerName) => {
        Swal.fire({
            title: `Rate ${lawyerName}`,
            html: `
                <div class="flex justify-center gap-1 my-4">
                    ${[1, 2, 3, 4, 5]
                    .map(
                        (i) => `
                        <button type="button" onclick="Swal.clickConfirm(); document.getElementById('rating').value = ${i}" class="text-3xl hover:scale-110 transition">
                            <span class="text-yellow-400">★</span>
                        </button>
                    `
                    )
                    .join("")}
                </div>
                <input type="hidden" id="rating" />
                <textarea id="comment" class="swal2-textarea mt-3" placeholder="Add a comment (optional)"></textarea>
            `,
            showCancelButton: true,
            confirmButtonText: "Submit Rating",
            preConfirm: () => {
                const rating = document.getElementById("rating").value;
                const comment = document.getElementById("comment").value;
                if (!rating) {
                    Swal.showValidationMessage("Please select a star rating");
                }
                return { rating: parseInt(rating), comment };
            },
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire("Thank You!", "Your rating has been submitted.", "success");
            }
        });
    };


    // 
    // 

    const handleDeleteFile = async (requestId, fileId) => {
        const confirm = await Swal.fire({
            title: "Delete File?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#ef4444",
        });

        if (!confirm.isConfirmed) return;

        try {
            await api.delete(`/requests/${requestId}/files/${fileId}`);
            await fetchRequests();
            if (showDetails?.id === requestId) {
                setShowDetails((prev) => ({
                    ...prev,
                    files: prev.files.filter((f) => f.id !== fileId),
                    files_count: prev.files_count - 1,
                }));
            }
            Swal.fire("Deleted!", "File has been removed.", "success");
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete file.";
            Swal.fire("Error", msg, "error");
        }
    };

    // تحديث الطلب
    const handleUpdateRequest = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("title", editingRequest.title);
        formData.append("description", editingRequest.description);

        const newFiles = fileInputRef.current?.files;
        if (newFiles && newFiles.length > 0) {
            for (let i = 0; i < newFiles.length; i++) {
                formData.append("files[]", newFiles[i]);
            }
        }

        Swal.fire({
            title: "Updating...",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const res = await api.post(`/requests/${editingRequest.id}?_method=PUT`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (res.data.success) {
                await fetchRequests();
                closeEditForm();
                Swal.fire("Updated!", "Your request has been updated.", "success");
            } else {
                throw new Error(res.data.message || "Unknown error");
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to update request.";
            Swal.fire("Error", msg, "error");
        }
    };


    // ألوان الحالة
    const getStatusConfig = (status) => {
        const config = {
            pending: { color: "bg-yellow-100 text-yellow-800", icon: <Clock className="w-3 h-3" /> },
            accepted: { color: "bg-blue-100 text-blue-800", icon: <CheckCircle className="w-3 h-3" /> },
            assigned: { color: "bg-purple-100 text-purple-800", icon: <UserCheck className="w-3 h-3" /> },
            completed: { color: "bg-green-100 text-green-800", icon: <Flag className="w-3 h-3" /> },
            rejected: { color: "bg-red-100 text-red-800", icon: <XCircle className="w-3 h-3" /> },
        };
        return config[status] || config.pending;
    };

    if (loading) {
        return (
            <section className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-24 bg-white rounded-xl"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">My Requests</h2>
                        <p className="text-gray-600 mt-1">Track and manage your consultation requests</p>
                    </div>
                </div>

                {/* No Requests */}
                {requests.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-600 font-medium">No requests yet</p>
                        <p className="text-gray-500 text-sm mt-1">Your submitted requests will appear here</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map((req, i) => {
                            const statusConfig = getStatusConfig(req.status);
                            return (
                                <motion.div
                                    key={req.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        {/* Lawyer Info */}
                                        <div className="flex items-center space-x-4 flex-1">
                                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-bold text-sm">
                                                    {req.lawyer_name
                                                        .split(" ")
                                                        .map((n) => n[0])
                                                        .slice(0, 2)
                                                        .join("")
                                                        .toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{req.lawyer_name}</h4>
                                                <p className="text-xs text-gray-500">Assigned: {req.assigned_to || "—"}</p>
                                            </div>
                                        </div>

                                        {/* Case Type & Price */}
                                        <div className="flex-1 text-center">
                                            <p className="text-sm font-medium text-gray-900">{req.case_type.name}</p>
                                            <p className="text-xs text-green-600 font-bold">
                                                {parseFloat(req.case_type.price).toFixed(2)} USD
                                            </p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 text-center">
                                                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    <FileText className="w-3 h-3" />
                                                    {req.files_count} file{req.files_count !== 1 ? "s" : ""}
                                                </div>
                                            </div>

                                            <div className="flex-1 text-center">
                                                <div
                                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                                                >
                                                    {statusConfig.icon}
                                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => openDetails(req)}
                                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>

                                            <button
                                                onClick={() => openEditForm(req)}
                                                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                title="Edit Request"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>

                                            {req.can_pay && (
                                                <button
                                                    onClick={() => handlePay(req.id)}
                                                    className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg font-medium text-xs hover:bg-green-200 flex items-center gap-1"
                                                >
                                                    <DollarSign className="w-3 h-3" />
                                                    Pay Now
                                                </button>
                                            )}

                                            {req.can_rate && (
                                                <button
                                                    onClick={() => handleRate(req.lawyer_name)}
                                                    className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg font-medium text-xs hover:bg-yellow-200 flex items-center gap-1"
                                                >
                                                    <Star className="w-3 h-3" />
                                                    Rate
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}

                {/* Details Modal */}
                {showDetails && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-xl font-bold text-gray-900">Request Details</h3>
                                <button onClick={closeDetails} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center space-x-4">
                                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-bold text-lg">
                                            {showDetails.lawyer_name
                                                .split(" ")
                                                .map((n) => n[0])
                                                .slice(0, 2)
                                                .join("")
                                                .toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{showDetails.lawyer_name}</h4>
                                        <p className="text-sm text-gray-600">Assigned to: {showDetails.assigned_to || "—"}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500">Title</p>
                                        <p className="font-medium text-gray-900">{showDetails.title}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Case Type</p>
                                        <p className="font-medium text-gray-900">{showDetails.case_type.name}</p>
                                        <p className="text-xs text-green-600 font-bold">
                                            {parseFloat(showDetails.case_type.price).toFixed(2)} USD
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-xs text-gray-500">Form</p>
                                    <p className="font-medium text-gray-900">{showDetails.form_name}</p>
                                </div>

                                <div>
                                    <p className="text-xs text-gray-500">Description</p>
                                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{showDetails.description}</p>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs text-gray-500">Submitted On</p>
                                        <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {format(new Date(showDetails.created_at), "MMMM d, yyyy 'at' h:mm a")}
                                        </p>
                                    </div>
                                    <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <FileText className="w-3 h-3" />
                                        {showDetails.files_count} file{showDetails.files_count !== 1 ? "s" : ""}
                                    </div>
                                </div>

                                {/* Attached Files */}
                                {showDetails.files && showDetails.files.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">Attached Files</p>
                                        <div className="space-y-2">
                                            {showDetails.files.map((file) => (
                                                <div
                                                    key={file.id}
                                                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-blue-600" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {file.size} • {file.type}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <a
                                                            href={file.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            Download
                                                        </a>
                                                        {/* زر الحذف دايمًا مرئي */}
                                                        <button
                                                            onClick={() => handleDeleteFile(showDetails.id, file.id)}
                                                            className="text-red-600 hover:text-red-800 p-1.5 bg-red-50 rounded-lg transition shadow-sm"
                                                            title="Delete File"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-between items-center pt-4">
                                    <div
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusConfig(
                                            showDetails.status
                                        ).color}`}
                                    >
                                        {getStatusConfig(showDetails.status).icon}
                                        {showDetails.status.charAt(0).toUpperCase() + showDetails.status.slice(1)}
                                    </div>
                                    <div className="flex gap-2">
                                        {/* زر التعديل الكبير */}
                                        <button
                                            onClick={() => openEditForm(showDetails)}
                                            className="px-5 py-2.5 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 flex items-center gap-2 shadow-md"
                                        >
                                            <Edit className="w-5 h-5" />
                                            Edit Request
                                        </button>

                                        {showDetails.can_pay && (
                                            <button
                                                onClick={() => handlePay(showDetails.id)}
                                                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-1"
                                            >
                                                <DollarSign className="w-4 h-4" />
                                                Pay Now
                                            </button>
                                        )}
                                        {showDetails.can_rate && (
                                            <button
                                                onClick={() => handleRate(showDetails.lawyer_name)}
                                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 flex items-center gap-1"
                                            >
                                                <Star className="w-4 h-4" />
                                                Rate Lawyer
                                            </button>
                                        )}
                                        <button
                                            onClick={closeDetails}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Edit Form Modal */}
                {showEditForm && editingRequest && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="text-xl font-bold text-gray-900">Edit Request</h3>
                                <button onClick={closeEditForm} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateRequest} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={editingRequest.title || ""}
                                        onChange={(e) =>
                                            setEditingRequest({ ...editingRequest, title: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        required
                                        rows={4}
                                        value={editingRequest.description || ""}
                                        onChange={(e) =>
                                            setEditingRequest({ ...editingRequest, description: e.target.value })
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Add New Files (optional)
                                    </label>
                                    <input
                                        type="file"
                                        multiple
                                        ref={fileInputRef}
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">You can upload additional documents.</p>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={closeEditForm}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center gap-1"
                                    >
                                        <Upload className="w-4 h-4" />
                                        Update Request
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </div>
        </section>
    );
}